import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CreditCard,
  Check,
  Crown,
  Zap,
  Sparkles,
  Settings,
  Infinity as InfinityIcon,
} from "lucide-react";
import {
  useCreateCheckoutSessionMutation,
  useCreateCustomerPortalSessionMutation,
} from "@/api/v1/payments";
import { useUserQuery } from "@/api/v1/user-api";
import { getPlanLimits } from "@/api/v1/subscription-types";
import { isUnlimited } from "@/api/v1/subscription-types";
import { toast } from "sonner";
import { useQueryState, parseAsString } from "nuqs";
import { useEffect } from "react";
import { FeatureFlags } from "@/feature_flags/feature_flag";
import { useFlag } from "@openfeature/react-sdk";
import { ComingSoon } from "@/components/coming-soon";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";

export const SubscriptionsRoute = () => {
  const queryClient = useQueryClient();
  const { data: user, isLoading: isLoadingUser } = useUserQuery();
  const createCheckoutSessionMutation = useCreateCheckoutSessionMutation();
  const createCustomerPortalSessionMutation =
    useCreateCustomerPortalSessionMutation();
  const [success, setSuccess] = useQueryState("success", parseAsString);
  const [canceled, setCanceled] = useQueryState("canceled", parseAsString);

  useEffect(() => {
    if (success === "true") {
      toast.success("Payment successful! Your subscription is now active.");
      setSuccess(null);
      // Invalidate user query to refresh subscription status
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
    } else if (canceled === "true") {
      toast.info("Payment canceled. You can try again anytime.");
      setCanceled(null);
    }
  }, [success, canceled, setSuccess, setCanceled, queryClient]);

  const handleCheckout = async () => {
    try {
      const successUrl = `${window.location.origin}/subscriptions?success=true`;
      const cancelUrl = `${window.location.origin}/subscriptions?canceled=true`;

      await createCheckoutSessionMutation.mutateAsync({
        successUrl,
        cancelUrl,
        product: "pro",
      });
    } catch (error) {
      console.error("Failed to create checkout session:", error);

      // Check if it's an axios error with 409 status (conflict - already has subscription)
      if (isAxiosError(error) && error.response?.status === 409) {
        const errorMessage =
          error.response.data?.error ||
          "You already have an active subscription";
        toast.error(errorMessage);
      } else {
        toast.error("Failed to create checkout session. Please try again.");
      }
    }
  };

  const handleManageSubscription = async () => {
    try {
      await createCustomerPortalSessionMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to create customer portal session:", error);

      if (isAxiosError(error) && error.response?.status === 400) {
        const errorMessage =
          error.response.data?.error ||
          "No billing account found. Please subscribe first.";
        toast.error(errorMessage);
      } else {
        toast.error("Failed to open customer portal. Please try again.");
      }
    }
  };

  type PlanType = "Free" | "Pro";

  // Active subscription statuses (user still has access)
  const activeStatuses = ["active", "trialing", "past_due", "incomplete"];
  const isSubscriptionActive = user?.subscription_status
    ? activeStatuses.includes(user.subscription_status)
    : false;

  // Determine plan: treat canceled/expired subscriptions as Free (no active subscription)
  // Only show Pro if subscription_plan_slug is "pro" AND status is one of the active statuses
  const currentPlan: PlanType =
    user?.subscription_plan_slug === "pro" && isSubscriptionActive
      ? "Pro"
      : "Free";

  const isSubscriptionCanceled = user?.subscription_status === "canceled";

  const proLimits = getPlanLimits("pro");

  const formatLimit = (limit: number, feature: string): string => {
    if (isUnlimited(limit)) {
      return `${feature} (Unlimited)`;
    }
    if (limit === 0) {
      return `${feature} (Not available)`;
    }
    // Handle pluralization
    const singular = feature.endsWith("s") ? feature.slice(0, -1) : feature;
    return `${feature} (${limit} ${limit === 1 ? singular : feature})`;
  };

  const freeFeatures = ["Flight restriction zones", "ADSB traffic"];

  const proFeatures = [
    formatLimit(proLimits.MaxGeofences, "Geofences"),
    formatLimit(proLimits.MaxFlightPlans, "Flight Plans"),
    "View drone remote ID detections from public sensors",
    "Access analytics",
    proLimits.EmailNotifications
      ? "Email notifications for geofence violations"
      : "No email notifications",
  ];

  const { value: isSubscriptionsEnabled } = useFlag(
    FeatureFlags.subscriptions_enabled,
    false
  );

  if (!isSubscriptionsEnabled) {
    return <ComingSoon />;
  }

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Subscriptions", href: "/subscriptions" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">Subscriptions</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Manage your subscription and billing
            </p>
          </div>
        </div>

        {/* Current Subscription Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Subscription
            </CardTitle>
            <CardDescription>
              Your current subscription status and plan details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingUser ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-muted p-3">
                      {currentPlan === "Pro" ? (
                        <Crown className="h-6 w-6 text-primary" />
                      ) : (
                        <Zap className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {currentPlan} Plan
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {isSubscriptionCanceled &&
                        user?.subscription_plan_ends_at
                          ? `Subscription canceled. Access until ${new Date(user.subscription_plan_ends_at).toLocaleDateString()}`
                          : currentPlan === "Pro"
                            ? user?.subscription_plan_ends_at
                              ? `Active until ${new Date(user.subscription_plan_ends_at).toLocaleDateString()}`
                              : "You're on the Pro plan with all premium features"
                            : "You're currently on the free plan"}
                      </p>
                    </div>
                  </div>
                  {currentPlan === "Pro" && isSubscriptionActive && (
                    <Badge
                      variant="default"
                      className="flex items-center gap-1"
                    >
                      <Crown className="h-3 w-3" />
                      {user?.subscription_status === "past_due"
                        ? "Past Due"
                        : "Active"}
                    </Badge>
                  )}
                  {isSubscriptionCanceled && (
                    <Badge
                      variant="destructive"
                      className="flex items-center gap-1"
                    >
                      Canceled
                    </Badge>
                  )}
                </div>
                {currentPlan === "Pro" && isSubscriptionActive && (
                  <Button
                    variant="outline"
                    onClick={handleManageSubscription}
                    disabled={createCustomerPortalSessionMutation.isPending}
                    className="w-full"
                  >
                    {createCustomerPortalSessionMutation.isPending ? (
                      <>
                        <Settings className="h-4 w-4 mr-2 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <Settings className="h-4 w-4 mr-2" />
                        Manage Subscription
                      </>
                    )}
                  </Button>
                )}
                {isSubscriptionCanceled && (
                  <Button
                    variant="outline"
                    onClick={handleManageSubscription}
                    disabled={createCustomerPortalSessionMutation.isPending}
                    className="w-full"
                  >
                    {createCustomerPortalSessionMutation.isPending ? (
                      <>
                        <Settings className="h-4 w-4 mr-2 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <Settings className="h-4 w-4 mr-2" />
                        Manage Subscription
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plans Comparison */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <Card className={currentPlan === "Free" ? "ring-2 ring-primary" : ""}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Free
                </CardTitle>
                {currentPlan === "Free" && (
                  <Badge variant="secondary">Current Plan</Badge>
                )}
              </div>
              <CardDescription>
                Perfect for getting started with airspace monitoring
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-3xl font-bold">$0</div>
                <div className="text-sm text-muted-foreground">per month</div>
              </div>
              <ul className="space-y-2">
                {freeFeatures.map((feature, index) => {
                  const isUnavailable = feature.includes("(Not available)");
                  return (
                    <li key={index} className="flex items-start gap-2">
                      {isUnavailable ? (
                        <span className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground">
                          ×
                        </span>
                      ) : (
                        <Check className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      )}
                      <span
                        className={`text-sm ${isUnavailable ? "text-muted-foreground line-through" : ""}`}
                      >
                        {feature}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {currentPlan === "Free" ? (
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              ) : (
                <Button variant="outline" className="w-full" disabled>
                  Downgrade
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card
            className={`relative ${currentPlan === "Pro" ? "ring-2 ring-primary" : "border-primary"}`}
          >
            {currentPlan !== "Pro" && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  Recommended
                </Badge>
              </div>
            )}
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />
                  Pro
                </CardTitle>
                {currentPlan === "Pro" && (
                  <Badge variant="default" className="flex items-center gap-1">
                    <Crown className="h-3 w-3" />
                    Active
                  </Badge>
                )}
              </div>
              <CardDescription>
                Unlock advanced features for professional airspace management
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-3xl font-bold">Custom</div>
                <div className="text-sm text-muted-foreground">
                  Contact us for pricing
                </div>
              </div>
              <ul className="space-y-2">
                {proFeatures.map((feature, index) => {
                  const hasUnlimitedText = feature.includes("(Unlimited)");
                  return (
                    <li key={index} className="flex items-start gap-2">
                      {hasUnlimitedText ? (
                        <InfinityIcon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      ) : (
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      )}
                      <span className="text-sm">{feature}</span>
                    </li>
                  );
                })}
              </ul>
              {currentPlan === "Pro" ? (
                <Button variant="default" className="w-full" disabled>
                  <Crown className="h-4 w-4 mr-2" />
                  Current Plan
                </Button>
              ) : (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={handleCheckout}
                  disabled={createCheckoutSessionMutation.isPending}
                >
                  {createCheckoutSessionMutation.isPending ? (
                    <>
                      <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Crown className="h-4 w-4 mr-2" />
                      Upgrade to Pro
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
};
